import { KitRequestSubtype } from 'views/kitting/Kit/KitRequestContext';
import { DecoratedKitRequestLine } from 'hooks/useGetDecoratedKitRequestLines';
import {
  AllocationReasonCode,
  AllocationReason,
  StockLotForAllocationReasoning,
  AllocationForReasoning,
} from 'ui-component/AllocatedPartsDatagrid/types';

const ALLOCATION_REASON_MESSAGES: Record<AllocationReasonCode, string> = {
  [AllocationReasonCode.TEAM_MISMATCH]:
    'Stock lot team does not match kit team',
  [AllocationReasonCode.TEAM_NULL_STOCK_LOT_ASSIGNED]:
    'Stock lot has team but kit has no team assigned',
  [AllocationReasonCode.TEAM_NULL_KIT_UNASSIGNED]:
    'Kit has team but stock lot has no team assigned',
  [AllocationReasonCode.TEAM_MATCH]: 'Team match allows allocation',
  [AllocationReasonCode.LOCATION_MISMATCH]:
    'Stock lot location not in kit source locations',
  [AllocationReasonCode.LOCATION_MATCH]:
    'Location matches kit source locations',
  [AllocationReasonCode.SMT_NO_PNP]: 'Stock lot is SMT no PNP',
  [AllocationReasonCode.QUANT_UNCERTAIN]: 'Stock lot quantity is uncertain',
  [AllocationReasonCode.ALREADY_ALLOCATED]:
    'Stock lot already allocated to different program or kit',
  [AllocationReasonCode.CURRENTLY_ALLOCATED]:
    'Currently allocated to this kit request',
  [AllocationReasonCode.LINE_LOCKED]: 'The line is locked',
  [AllocationReasonCode.AVAILABLE]: 'Available for allocation',
};

/**
 * Computes whether a stock lot can be allocated to a kit request and provides a reason
 *
 * @param stockLot - The stock lot to evaluate for allocation
 * @param kitRequest - The kit request context
 * @param options - Additional options including sourceLocationIds and kitRequestLine
 * @returns AllocationReason object with canAllocate boolean and reason string
 */
export const computeAllocationReasons = (
  stockLot: StockLotForAllocationReasoning,
  kitRequest: KitRequestSubtype | null | undefined,
  options?: {
    sourceLocationIds?: string[];
    kitRequestLine?: DecoratedKitRequestLine;
    isAllocated?: boolean;
    allocations?: AllocationForReasoning[];
  }
): AllocationReason => {
  const allReasons: string[] = [];
  let canAllocate = true;
  let primaryReasonCode = AllocationReasonCode.AVAILABLE;

  const kitRequestLine = options?.kitRequestLine;
  const isAllocatedToCurrentLine =
    options?.isAllocated ||
    (kitRequestLine &&
      options?.allocations &&
      options.allocations.some(
        (allocation) =>
          allocation.stockLot === stockLot.id &&
          allocation.kitRequestLine === kitRequestLine.id
      ));

  if (isAllocatedToCurrentLine) {
    allReasons.push(
      ALLOCATION_REASON_MESSAGES[AllocationReasonCode.CURRENTLY_ALLOCATED]
    );
    primaryReasonCode = AllocationReasonCode.CURRENTLY_ALLOCATED;
  } else if (options?.kitRequestLine?.locked) {
    allReasons.push(
      ALLOCATION_REASON_MESSAGES[AllocationReasonCode.LINE_LOCKED]
    );
    canAllocate = false;
    primaryReasonCode = AllocationReasonCode.LINE_LOCKED;
    return {
      canAllocate: false,
      reason: ALLOCATION_REASON_MESSAGES[AllocationReasonCode.LINE_LOCKED],
      reasonCode: AllocationReasonCode.LINE_LOCKED,
      allReasons,
    };
  }

  const teamReason = checkTeamCompatibility(stockLot, kitRequest);
  if (!teamReason.canAllocate) {
    allReasons.push(teamReason.reason);
    if (canAllocate) {
      canAllocate = false;
      primaryReasonCode = teamReason.reasonCode;
    }
  }

  const locationReason = checkLocationCompatibility(
    stockLot,
    kitRequest,
    options?.sourceLocationIds
  );
  if (!locationReason.canAllocate) {
    allReasons.push(locationReason.reason);
    if (canAllocate && !isAllocatedToCurrentLine) {
      canAllocate = false;
      primaryReasonCode = locationReason.reasonCode;
    }
  }

  if (stockLot.smtNoPnp) {
    const smtReason =
      ALLOCATION_REASON_MESSAGES[AllocationReasonCode.SMT_NO_PNP];
    allReasons.push(smtReason);
    if (canAllocate && !isAllocatedToCurrentLine) {
      canAllocate = false;
      primaryReasonCode = AllocationReasonCode.SMT_NO_PNP;
    }
  }

  if (!stockLot.quantCertain) {
    const quantUncertainReason =
      ALLOCATION_REASON_MESSAGES[AllocationReasonCode.QUANT_UNCERTAIN];
    allReasons.push(quantUncertainReason);
    if (canAllocate && !isAllocatedToCurrentLine) {
      canAllocate = false;
      primaryReasonCode = AllocationReasonCode.QUANT_UNCERTAIN;
    }
  }

  const allocationReason = checkExistingAllocation(
    stockLot,
    kitRequest,
    options?.kitRequestLine,
    options?.allocations
  );
  if (!allocationReason.canAllocate) {
    allReasons.push(allocationReason.reason);
    if (canAllocate && !isAllocatedToCurrentLine) {
      canAllocate = false;
      primaryReasonCode = allocationReason.reasonCode;
    }
  }

  if (isAllocatedToCurrentLine && allReasons.length > 1) {
    canAllocate = true;
    primaryReasonCode = AllocationReasonCode.CURRENTLY_ALLOCATED;
  }

  if (allReasons.length === 0) {
    allReasons.push(ALLOCATION_REASON_MESSAGES[AllocationReasonCode.AVAILABLE]);
  }

  const primaryReason =
    allReasons.length === 1
      ? allReasons[0]
      : `${allReasons[0]}${
          allReasons.length > 1 ? ` (+${allReasons.length - 1} more)` : ''
        }`;

  return {
    canAllocate,
    reason: primaryReason,
    reasonCode: primaryReasonCode,
    allReasons,
  };
};

/**
 * Checks team compatibility between stock lot and kit request
 */
const checkTeamCompatibility = (
  stockLot: StockLotForAllocationReasoning,
  kitRequest: KitRequestSubtype | null | undefined
): AllocationReason => {
  const stockLotTeamId = stockLot.team;
  const kitTeamId = kitRequest?.team?.id;

  if (stockLotTeamId && (!kitRequest || !kitTeamId)) {
    return {
      canAllocate: false,
      reason:
        ALLOCATION_REASON_MESSAGES[
          AllocationReasonCode.TEAM_NULL_STOCK_LOT_ASSIGNED
        ],
      reasonCode: AllocationReasonCode.TEAM_NULL_STOCK_LOT_ASSIGNED,
    };
  }

  if (stockLotTeamId && kitTeamId && stockLotTeamId !== kitTeamId) {
    return {
      canAllocate: false,
      reason: ALLOCATION_REASON_MESSAGES[AllocationReasonCode.TEAM_MISMATCH],
      reasonCode: AllocationReasonCode.TEAM_MISMATCH,
    };
  }

  return {
    canAllocate: true,
    reason: ALLOCATION_REASON_MESSAGES[AllocationReasonCode.TEAM_MATCH],
    reasonCode: AllocationReasonCode.TEAM_MATCH,
  };
};

/**
 * Checks location compatibility between stock lot and kit request source locations
 * Note: KitRequestSubtype excludes sourceLocations, so this check may need to be done externally
 */
const checkLocationCompatibility = (
  stockLot: StockLotForAllocationReasoning,
  kitRequest: KitRequestSubtype | null | undefined,
  sourceLocationIds?: string[]
): AllocationReason => {
  if (!sourceLocationIds || sourceLocationIds.length === 0) {
    return {
      canAllocate: true,
      reason: ALLOCATION_REASON_MESSAGES[AllocationReasonCode.LOCATION_MATCH],
      reasonCode: AllocationReasonCode.LOCATION_MATCH,
    };
  }

  const stockLotFacilityId = stockLot.facility;

  if (stockLotFacilityId && sourceLocationIds.includes(stockLotFacilityId)) {
    return {
      canAllocate: true,
      reason: ALLOCATION_REASON_MESSAGES[AllocationReasonCode.LOCATION_MATCH],
      reasonCode: AllocationReasonCode.LOCATION_MATCH,
    };
  }

  return {
    canAllocate: false,
    reason: ALLOCATION_REASON_MESSAGES[AllocationReasonCode.LOCATION_MISMATCH],
    reasonCode: AllocationReasonCode.LOCATION_MISMATCH,
  };
};

/**
 * Checks if stock lot is already allocated to a different program or kit
 */
const checkExistingAllocation = (
  stockLot: StockLotForAllocationReasoning,
  kitRequest: KitRequestSubtype | null | undefined,
  kitRequestLine?: DecoratedKitRequestLine,
  allocations?: AllocationForReasoning[]
): AllocationReason => {
  if (!allocations || allocations.length === 0) {
    return {
      canAllocate: true,
      reason: ALLOCATION_REASON_MESSAGES[AllocationReasonCode.AVAILABLE],
      reasonCode: AllocationReasonCode.AVAILABLE,
    };
  }

  // Filter allocations to only those for this specific stock lot
  const stockLotAllocations = allocations.filter(
    (allocation) => allocation.stockLot === stockLot.id
  );

  if (stockLotAllocations.length === 0) {
    return {
      canAllocate: true,
      reason: ALLOCATION_REASON_MESSAGES[AllocationReasonCode.AVAILABLE],
      reasonCode: AllocationReasonCode.AVAILABLE,
    };
  }

  // Check if already allocated to current kit request line
  if (kitRequestLine) {
    const isAllocatedToCurrentLine = stockLotAllocations.some(
      (allocation) => allocation.kitRequestLine === kitRequestLine.id
    );

    if (isAllocatedToCurrentLine) {
      return {
        canAllocate: true,
        reason:
          ALLOCATION_REASON_MESSAGES[AllocationReasonCode.CURRENTLY_ALLOCATED],
        reasonCode: AllocationReasonCode.CURRENTLY_ALLOCATED,
      };
    }
  }

  // Check if allocated to other kit requests or production runs
  const hasOtherAllocations = stockLotAllocations.some((allocation) => {
    // Skip allocations with zero quantity
    if (allocation.quant <= 0) return false;

    // Check if it's allocated to a different kit request
    if (allocation.kitRequestLine) {
      // If we have a current kit request line, exclude allocations to it
      if (kitRequestLine && allocation.kitRequestLine === kitRequestLine.id) {
        return false;
      }

      return true;
    }

    // Check if it's allocated to a production run
    if (allocation.productionRun) {
      // If current kit request is for the same production run, allow it
      if (
        kitRequest?.productionRun?.id &&
        allocation.productionRun === kitRequest.productionRun.id
      ) {
        return false;
      }

      return true;
    }

    return false;
  });

  if (hasOtherAllocations) {
    return {
      canAllocate: false,
      reason:
        ALLOCATION_REASON_MESSAGES[AllocationReasonCode.ALREADY_ALLOCATED],
      reasonCode: AllocationReasonCode.ALREADY_ALLOCATED,
    };
  }

  return {
    canAllocate: true,
    reason: ALLOCATION_REASON_MESSAGES[AllocationReasonCode.AVAILABLE],
    reasonCode: AllocationReasonCode.AVAILABLE,
  };
};
